import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { INSPECT_STATUS_UPCOMING } from 'config/constants';

import './style.css';

// Maps a stored column-filter value (array, scalar string, or the upcoming
// sentinel) to the array of option values the picker opens with checked.
// Unknown values are dropped -- applying stores exactly the checked set.
export function normalizeOptionFilterValue(value, options) {
  var known = {};
  for (var i = 0; i < options.length; i++) {
    known[options[i].value] = true;
  }
  var source;
  if (Array.isArray(value)) {
    source = value;
  } else if (value === INSPECT_STATUS_UPCOMING) {
    // The Upcoming KPI preset spans two statuses; the picker shows both
    // checked (the filter DTO treats the two forms identically).
    source = ['due-soon', 'scheduled'];
  } else if (typeof value === 'string' && value !== '') {
    source = [value];
  } else {
    source = [];
  }
  var selected = [];
  for (var s = 0; s < source.length; s++) {
    if (known[source[s]] && selected.indexOf(source[s]) === -1) {
      selected.push(source[s]);
    }
  }
  return selected;
}

// Multi-select option picker for columns with a fixed value set. Same portal,
// overlay, positioning, and visual language as FilterPopover; the option rows
// reuse the table's inspect-result-badge chips so the picker reads as
// "choose which chips to show". Selection applies on Apply (each apply
// triggers a server refetch), not per-toggle.
class OptionFilterPopover extends Component {
  constructor(props) {
    super(props);
    this.popoverRef = React.createRef();
    this.state = {
      selected: normalizeOptionFilterValue(props.value, props.options),
    };
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleClickOutside(e) {
    if (
      this.popoverRef.current &&
      !this.popoverRef.current.contains(e.target)
    ) {
      this.props.onClose();
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.props.onClose();
    } else if (e.key === 'Enter') {
      this.handleApply();
    }
  }

  handleToggle(optionValue) {
    var selected = this.state.selected.slice();
    var at = selected.indexOf(optionValue);
    if (at === -1) {
      selected.push(optionValue);
    } else {
      selected.splice(at, 1);
    }
    this.setState({ selected: selected });
  }

  handleApply() {
    this.props.onApply(this.state.selected);
  }

  handleClear() {
    this.props.onApply([]);
  }

  render() {
    var props = this.props;
    var t = props.t;
    var anchorRect = props.anchorRect;
    if (!anchorRect) return null;

    // Below the filter icon, right-aligned, clamped to the viewport --
    // matches FilterPopover's placement.
    var popoverWidth = 220;
    var style = {
      top: anchorRect.bottom + 6,
      left: Math.max(8, anchorRect.right - popoverWidth),
    };
    if (style.left + popoverWidth > window.innerWidth - 8) {
      style.left = window.innerWidth - popoverWidth - 8;
    }

    var selected = this.state.selected;
    var self = this;

    return ReactDOM.createPortal(
      <div className="option-filter-popover-overlay">
        <div
          className="option-filter-popover"
          ref={this.popoverRef}
          style={style}
        >
          <div className="option-filter-options">
            {props.options.map(function (option) {
              var isChecked = selected.indexOf(option.value) !== -1;
              return (
                <div
                  key={option.value}
                  className="option-filter-row"
                  onClick={function () {
                    self.handleToggle(option.value);
                  }}
                >
                  <span
                    className={
                      'option-filter-checkbox' + (isChecked ? ' checked' : '')
                    }
                  >
                    <span className="material-symbols-outlined">check</span>
                  </span>
                  <span
                    className={'inspect-result-badge ' + option.badgeClassName}
                  >
                    {option.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="option-filter-footer">
            <button
              type="button"
              className="option-filter-clear"
              onClick={this.handleClear}
            >
              {t('btn:clear')}
            </button>
            <button
              type="button"
              className="filter-popover-apply"
              onClick={this.handleApply}
            >
              {t('btn:apply')}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
}

export default withTranslation()(OptionFilterPopover);
